import React, { useEffect, useState } from "react";
import api from "../services/api";
import { jwtDecode } from "jwt-decode";
import Swal from "sweetalert2";
import { useFiscal } from "../hooks/useFiscal";
import { obtenerFechaLocal } from "../utils/dateUtils";

const EntradaFactura = () => {
  const fiscal = useFiscal();
  const [proveedor, setProveedor] = useState("");
  const [numeroFactura, setNumeroFactura] = useState("");
  const [productos, setProductos] = useState([]);
  const [materiales, setMateriales] = useState([]);
  const [mensaje, setMensaje] = useState({ texto: "", tipo: "" });
  const [guardando, setGuardando] = useState(false);
  const [usuario, setUsuario] = useState(null); 
  const [categorias, setCategorias] = useState([]);

  const [fechaAdquisicion, setFechaAdquisicion] = useState(obtenerFechaLocal());

  // OBTENER USUARIO Y DATOS
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const [resMateriales, resCategorias] = await Promise.all([
          api.get("/materiales"),
          api.get("/categorias"),
        ]);
        
        setMateriales(resMateriales.data);
        setCategorias(resCategorias.data);
      } catch (e) {
        console.error("Error cargando datos:", e);
        setMensaje({ texto: "❌ Error al cargar materiales y categorías", tipo: "error" });
      }
    };

    const getUsuario = () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const decoded = jwtDecode(token);
          setUsuario(decoded);
        } catch (error) {
          console.error("Error al decodificar token:", error);
        }
      }
    };
    
    cargarDatos();
    getUsuario();
  }, []);

  // ✅ AGREGAR FILA CON CATEGORÍA PROPIA
  const agregarFila = () => {
    setProductos((prev) => [
      ...prev, 
      { 
        categoria_id: "",      // ← Cada producto tiene su categoría
        codigo: "", 
        cantidad: "", 
        costo_unitario: "", 
        itbis: 0 
      }
    ]);
  };

  const quitarFila = (idx) => {
    setProductos((prev) => prev.filter((_, i) => i !== idx));
  };

  // ✅ ACTUALIZAR CAMPO DE UN PRODUCTO ESPECÍFICO
  const setCampo = (index, campo, valor) => {
    setProductos((prev) => prev.map((producto, i) => {
      if (i === index) {
        // Si cambia la categoría, resetear el código del material
        if (campo === 'categoria_id') {
          return {
            ...producto,
            categoria_id: valor,
            codigo: ''  // Resetear material al cambiar categoría
          };
        }
        
        // Para otros campos
        return {
          ...producto,
          [campo]: valor
        };
      }
      return producto;
    }));
  };

  // ✅ FILTRAR MATERIALES POR CATEGORÍA DE UN PRODUCTO ESPECÍFICO
  const getMaterialesPorCategoria = (categoriaId) => {
    if (!categoriaId) {
      return materiales; // Mostrar todos si no hay categoría seleccionada
    }
    return materiales.filter((m) => m.CategoriaId === parseInt(categoriaId));
  };

  // FUNCIÓN PARA CALCULAR EL VALOR TOTAL DE UNA FILA (CON ITBIS)
  const calcularValorTotalFila = (p) => {
    const cantidad = Number(p.cantidad) || 0;
    const costo_unitario = Number(p.costo_unitario) || 0;
    const itbis_percent = Number(p.itbis || 0) / 100;
    
    const costo_con_itbis = costo_unitario * (1 + itbis_percent);
    return costo_con_itbis * cantidad;
  };

  // TOTAL FACTURA ACTUALIZADO
  const totalFactura = productos.reduce(
    (acc, p) => acc + calcularValorTotalFila(p),
    0
  );

  const guardarFactura = async () => {
    try {
      if (!proveedor || !numeroFactura || !fechaAdquisicion) {
        setMensaje({ texto: "⚠️ Por favor completa proveedor, número de factura y fecha", tipo: "error" });
        return;
      }
      if (productos.length === 0) {
        setMensaje({ texto: "⚠️ Agrega al menos un producto a la factura", tipo: "error" });
        return;
      }
      const itemsValidos = productos.filter(
        (p) => p.codigo && Number(p.cantidad) > 0 && Number(p.costo_unitario) >= 0
      );
      if (itemsValidos.length === 0) {
        setMensaje({ texto: "⚠️ Verifica que los productos tengan código, cantidad y costo válidos", tipo: "error" });
        return;
      }

      setGuardando(true);
      setMensaje({ texto: "", tipo: "" });

      const payload = {
        proveedor,
        numero_factura: numeroFactura,
        fecha_adquisicion: fechaAdquisicion,
        responsable: usuario?.nombre || usuario?.email || "Usuario (Factura)",
        productos: itemsValidos.map(p => ({
          codigo: p.codigo,
          cantidad: Number(p.cantidad),
          costo_unitario: Number(p.costo_unitario),
          itbis: Number(p.itbis || 0)
        })),
      };

      const res = await api.post("/entradas/factura", payload);
      
      Swal.fire({
        icon: 'success',
        title: '✅ Factura Importada',
        html: `
          <p style="font-size: 15px; margin: 10px 0;">
            ${res.data?.mensaje || 'La factura ha sido importada correctamente.'}
          </p>
          <div style="background: #fef3c7; padding: 15px; border-radius: 8px; margin-top: 15px; border-left: 4px solid #f59e0b;">
            <p style="margin: 0; color: #78350f; font-weight: 600;">
              ⏳ <strong>Pendiente de Aprobación</strong>
            </p>
            <p style="margin: 5px 0 0 0; color: #78350f; font-size: 13px;">
              El stock se actualizará una vez que el supervisor apruebe esta entrada.
            </p>
          </div>
        `,
        confirmButtonColor: '#667eea',
        confirmButtonText: 'Entendido'
      });
      
      // Limpiar formulario
      setProveedor("");
      setNumeroFactura("");
      setFechaAdquisicion(obtenerFechaLocal());
      setProductos([]);
      
    } catch (e) {
      console.error(e);
      setMensaje({ texto: "❌ Error al registrar la factura", tipo: "error" }); 
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>🧾 Registrar Entrada por Factura</h1>
        <p style={styles.subtitle}>Registra múltiples materiales desde una factura</p>
      </div>

      {/* Información de la Factura */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>📋 Información de la Factura</h3>
        <div style={styles.formGrid}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Proveedor *</label>
            <input
              style={styles.input}
              type="text"
              placeholder="Nombre del proveedor"
              value={proveedor}
              onChange={(e) => setProveedor(e.target.value)}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Número de Factura *</label>
            <input
              style={styles.input}
              type="text"
              placeholder="001-0000001"
              value={numeroFactura}
              onChange={(e) => setNumeroFactura(e.target.value)}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Fecha de Adquisición *</label>
            <input
              style={styles.input}
              type="date"
              value={fechaAdquisicion}
              onChange={(e) => setFechaAdquisicion(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Tabla de productos */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>📦 Productos de la Factura</h3>
          <button style={styles.btnAdd} onClick={agregarFila}>
            ➕ Agregar Producto
          </button>
        </div>

        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Categoría</th>
                <th style={styles.th}>Material</th>
                <th style={styles.th}>Cantidad</th>
                <th style={styles.th}>Costo Unit.</th>
                <th style={styles.th}>{fiscal.impuesto_label || 'ITBIS'} %</th>
                <th style={styles.th}>Valor Total</th>
                <th style={styles.th}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {productos.length === 0 ? (
                <tr>
                  <td colSpan="7" style={styles.emptyRow}>
                    📦 No hay productos agregados. Haz clic en "Agregar Producto" para comenzar.
                  </td>
                </tr>
              ) : (
                productos.map((p, i) => {
                  // ✅ Obtener materiales filtrados POR ESTE PRODUCTO
                  const materialesFiltrados = getMaterialesPorCategoria(p.categoria_id);
                  
                  return (
                    <tr key={i} style={styles.tr}>
                      {/* ✅ CATEGORÍA - Independiente por producto */}
                      <td style={styles.td}>
                        <select
                          style={styles.select}
                          value={p.categoria_id || ""}
                          onChange={(e) => setCampo(i, 'categoria_id', e.target.value)}
                        >
                          <option value="">Seleccione categoría</option>
                          {categorias.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              {cat.nombre}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* ✅ MATERIAL - Filtrado por la categoría de ESTE producto */}
                      <td style={styles.td}>
                        <select
                          style={styles.select}
                          value={p.codigo || ""}
                          onChange={(e) => setCampo(i, 'codigo', e.target.value)}
                          disabled={!p.categoria_id} // Solo habilitar si hay categoría
                        >
                          <option value="">
                            {p.categoria_id 
                              ? `Seleccione material (${materialesFiltrados.length})`
                              : "Primero seleccione categoría"
                            }
                          </option>
                          {materialesFiltrados.map((m) => (
                            <option key={m.id} value={m.codigo}>
                              {m.codigo} - {m.nombre}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Cantidad */}
                      <td style={styles.td}>
                        <input
                          style={styles.inputTable}
                          type="number"
                          placeholder="0"
                          value={p.cantidad}
                          onChange={(e) => setCampo(i, 'cantidad', e.target.value)}
                        />
                      </td>

                      {/* Costo Unitario */}
                      <td style={styles.td}>
                        <input
                          style={styles.inputTable}
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={p.costo_unitario}
                          onChange={(e) => setCampo(i, 'costo_unitario', e.target.value)}
                        />
                      </td>

                      {/* ITBIS */}
                      <td style={styles.td}>
                        <select
                          style={styles.select}
                          value={p.itbis || 0}
                          onChange={(e) => setCampo(i, 'itbis', e.target.value)}
                        >
                          <option value="0">0%</option>
                          <option value="18">18%</option>
                        </select>
                      </td>

                      {/* Valor Total */}
                      <td style={{...styles.td, textAlign: 'right', fontWeight: 'bold', color: '#2196F3'}}>
                        RD$ {calcularValorTotalFila(p).toFixed(2)}
                      </td>

                      {/* Acción */}
                      <td style={styles.td}>
                        <button
                          style={styles.btnDelete}
                          onClick={() => quitarFila(i)}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#dc2626'}
                          onMouseLeave={(e) => e.currentTarget.style.background = '#ef4444'}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Total */}
        {productos.length > 0 && (
          <div style={styles.totalSection}>
            <div style={styles.totalLabel}>Total de la Factura:</div>
            <div style={styles.totalValue}>RD$ {totalFactura.toFixed(2)}</div>
          </div>
        )}

        {/* Botón Guardar */}
        <div style={styles.actions}>
          <button
            style={{...styles.btnSave, opacity: guardando ? 0.6 : 1}}
            onClick={guardarFactura}
            disabled={guardando}
          >
            {guardando ? "Guardando..." : "💾 Guardar Factura"}
          </button>
        </div>

        {/* Mensaje */}
        {mensaje.texto && mensaje.tipo === "error" && (
          <div style={{
            ...styles.message,
            ...styles.messageError
          }}>
            {mensaje.texto}
          </div>
        )}
      </div>

      {/* Información de ayuda */}
      <div style={styles.helpCard}>
        <p style={styles.helpText}>
          💡 <strong>Tip:</strong> Selecciona primero la categoría, luego aparecerán solo los materiales de esa categoría.
        </p>
        <p style={styles.helpText}>
          ⏳ <strong>Nota:</strong> Las entradas registradas quedarán pendientes de aprobación por el supervisor.
        </p>
      </div>
    </div>
  );
};

// Estilos (sin cambios)
const styles = {
  container: {
    minHeight: '100vh',
    background: '#f8fafc',
    padding: '20px',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  header: {
    marginBottom: '25px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 5px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: '0',
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '25px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e2e8f0',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '15px',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 20px 0',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569',
    marginBottom: '6px',
  },
  input: {
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    outline: 'none',
    transition: 'all 0.2s ease',
  },
  btnAdd: {
    padding: '10px 20px',
    background: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  tableWrapper: {
    overflowX: 'auto',
    marginBottom: '20px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  th: {
    background: '#f8fafc',
    padding: '12px',
    textAlign: 'left',
    fontWeight: '700',
    color: '#475569',
    borderBottom: '2px solid #e2e8f0',
    whiteSpace: 'nowrap',
  },
  tr: {
    transition: 'background 0.2s ease',
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #f1f5f9',
  },
  select: {
    width: '100%',
    padding: '8px',
    fontSize: '13px',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    outline: 'none',
    background: 'white',
    cursor: 'pointer',
  },
  inputTable: {
    width: '100%',
    padding: '8px',
    fontSize: '13px',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    outline: 'none',
  },
  btnDelete: {
    padding: '6px 12px',
    background: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '14px',
  },
  emptyRow: {
    padding: '40px',
    textAlign: 'center',
    color: '#94a3b8',
    fontStyle: 'italic',
  },
  totalSection: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '20px',
    padding: '20px',
    background: '#f8fafc',
    borderRadius: '8px',
    marginBottom: '20px',
  },
  totalLabel: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#475569',
  },
  totalValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#10b981',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  btnSave: {
    padding: '12px 32px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
  },
  message: {
    padding: '12px 20px',
    borderRadius: '8px',
    marginTop: '20px',
    fontSize: '14px',
    fontWeight: '500',
    textAlign: 'center',
  },
  messageSuccess: {
    background: '#d1fae5',
    color: '#065f46',
    border: '1px solid #10b981',
  },
  messageError: {
    background: '#fee2e2',
    color: '#991b1b',
    border: '1px solid #ef4444',
  },
  helpCard: {
    background: '#eff6ff',
    border: '1px solid #3b82f6',
    borderRadius: '12px',
    padding: '15px 20px',
  },
  helpText: {
    margin: '0 0 10px 0',
    fontSize: '13px',
    color: '#1e40af',
    lineHeight: '1.6',
  },
};

export default EntradaFactura;